import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Select, Table, Checkbox, Button, Space, Spin, message } from 'antd';
import { useGetAllShootTypes, usePutStyleColorRefSettings, useGetCompanyConfig, useGetStyleColorRefSettings } from '../../hooks/useCompanies';
import useLogout from '../../hooks/useLogout';

const StyleColorLibraryModal = ({ record, onClose, onSave: customOnSave, inline = false, orderId }) => {
    const { logout } = useLogout();
    const [styleColumnName, setStyleColumnName] = useState('');
    const [colorColumnName, setColorColumnName] = useState('');
    const [checkboxStates, setCheckboxStates] = useState({});
    const [companyConfigData, setCompanyConfigData] = useState(null);
    const [settingsData, setSettingsData] = useState(null);
    const settingsAppliedRef = useRef(false);

    // Determine if we're in order mode
    const isOrderMode = !!orderId;
    // Extract companyId from record
    // In order mode: try to get companyId from record.company or record.companyId
    // In company mode: use record.key or record._id
    const companyId = isOrderMode 
        ? (record?.company || record?.companyId || null)
        : (record?.key || record?._id);

    // Fetch company config (when companyId is available, regardless of mode)
    const onConfigSuccess = data => {
        setCompanyConfigData(data);
    };
    const { isLoading: isConfigLoading } = useGetCompanyConfig(onConfigSuccess, companyId, !!companyId);

    // Fetch style color reference settings
    const onSettingsSuccess = response => {
        console.log(response);
        if (response) {
            setSettingsData(response?.data);
            setStyleColumnName(response?.data?.styleColumnName || '');
            setColorColumnName(response?.data?.colorColumnName || '');
            // Reset the ref when new settings are loaded
            settingsAppliedRef.current = false;
        }
    };
    const { isLoading: isSettingsLoading } = useGetStyleColorRefSettings(onSettingsSuccess, companyId, orderId);

    // Reset ref when companyId or orderId changes
    useEffect(() => {
        settingsAppliedRef.current = false;
    }, [companyId, orderId]);

    const { data: shootTypesData, isLoading } = useGetAllShootTypes();

    const onSaveSuccess = response => {
        message.success('Style Color Reference Settings saved successfully');
        onClose();
    };

    const onSaveError = error => {
        if (error?.response?.status === 401) {
            logout();
        } else {
            const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred';
            message.error(`Failed to save Style Color Reference Settings: ${errorMessage}`);
        }
    };

    const { mutate: saveStyleColorRefSettings, isLoading: isSaving } = usePutStyleColorRefSettings(
        onSaveSuccess,
        onSaveError
    );

    // Extract columnOptions from company config data
    // Extract all column names from columnHeaders[].name[] arrays, flatten and deduplicate
    const getColumnOptions = () => {
        if (!companyConfigData?.columnHeaders || !Array.isArray(companyConfigData.columnHeaders)) {
            // Fallback to default options if config is not available
            return [
                { value: 'Style_Code', label: 'Style_Code' },
                { value: 'Color_Code', label: 'Color_Code' },
                { value: 'Style_Name', label: 'Style_Name' },
                { value: 'Color_Name', label: 'Color_Name' },
            ];
        }

        // Extract all column names from columnHeaders
        const allColumnNames = companyConfigData.columnHeaders
            .flatMap(header => header.name || [])
            .filter(name => name && name.trim() !== ''); // Filter out empty strings

        // Remove duplicates and create options
        const uniqueNames = Array.from(new Set(allColumnNames));
        return uniqueNames.map(name => ({
            value: name,
            label: name,
        }));
    };

    const columnOptions = getColumnOptions();

    // Transform API response to array of shoot type names
    // Handle both cases: shootTypesData is an array, or shootTypesData.data is an array
    // Memoize shootTypes to prevent infinite loop in useEffect
    const shootTypes = useMemo(() => {
        const shootTypesArray = Array.isArray(shootTypesData)
            ? shootTypesData
            : shootTypesData?.data || [];

        return shootTypesArray.map(item =>
            typeof item === 'string' ? item : item.name || item
        );
    }, [shootTypesData]);

    // Initialize checkboxStates when shoot types are loaded and populate from settings if available
    useEffect(() => {
        if (shootTypes.length > 0) {
            setCheckboxStates(prev => {
                // If settings data exists and has shootTypeSettings, populate from it
                // Only apply settings once using ref to prevent re-applying
                if (settingsData?.shootTypeSettings && Array.isArray(settingsData.shootTypeSettings) && !settingsAppliedRef.current) {
                    const statesFromSettings = {};
                    // First, populate from settings
                    settingsData.shootTypeSettings.forEach(setting => {
                        if (setting.shootType) {
                            statesFromSettings[setting.shootType] = {
                                styleCheck: setting.styleCheck || false,
                                colorCheck: setting.colorCheck || false,
                            };
                        }
                    });
                    // Then, ensure all shoot types exist (add missing ones with default false)
                    shootTypes.forEach(shootType => {
                        if (!statesFromSettings[shootType]) {
                            statesFromSettings[shootType] = {
                                styleCheck: false,
                                colorCheck: false,
                            };
                        }
                    });
                    settingsAppliedRef.current = true;
                    return statesFromSettings;
                }

                // If no settings data or settings already applied, initialize with defaults
                // Only initialize if checkboxStates is empty
                if (Object.keys(prev).length === 0) {
                    const initialStates = {};
                    shootTypes.forEach(shootType => {
                        initialStates[shootType] = {
                            styleCheck: false,
                            colorCheck: false,
                        };
                    });
                    return initialStates;
                }
                // Otherwise, ensure all shoot types exist in state
                const updatedStates = { ...prev };
                let hasChanges = false;
                shootTypes.forEach(shootType => {
                    if (!updatedStates[shootType]) {
                        updatedStates[shootType] = {
                            styleCheck: false,
                            colorCheck: false,
                        };
                        hasChanges = true;
                    }
                });
                // Only return new state if there are actual changes
                return hasChanges ? updatedStates : prev;
            });
        }
    }, [shootTypes, settingsData]);

    const handleStyleCheckChange = (shootType, checked) => {
        setCheckboxStates(prev => ({
            ...prev,
            [shootType]: {
                ...prev[shootType],
                styleCheck: checked,
            },
        }));
    };

    const handleColorCheckChange = (shootType, checked) => {
        setCheckboxStates(prev => ({
            ...prev,
            [shootType]: {
                ...prev[shootType],
                colorCheck: checked,
            },
        }));
    };

    const handleSave = () => {
        // Use orderId if in order mode, otherwise extract companyId from record
        const id = isOrderMode ? orderId : (record?.key || record?._id);

        if (!id) {
            message.warning(isOrderMode ? 'Order ID not found: Unable to save settings. Please try again.' : 'Company ID not found: Unable to save settings. Please try again.');
            return;
        }

        // Transform checkboxStates object into shootTypeSettings array
        const shootTypeSettings = shootTypes.map(shootType => ({
            shootType: shootType,
            styleCheck: checkboxStates[shootType]?.styleCheck || false,
            colorCheck: checkboxStates[shootType]?.colorCheck || false,
        }));

        // Prepare payload - use orderId if in order mode, otherwise companyId
        const payload = isOrderMode
            ? {
                  orderId: orderId,
                  styleColumnName: styleColumnName,
                  colorColumnName: colorColumnName,
                  shootTypeSettings: shootTypeSettings,
              }
            : {
                  companyId: companyId,
                  styleColumnName: styleColumnName,
                  colorColumnName: colorColumnName,
                  shootTypeSettings: shootTypeSettings,
              };

        // If custom onSave is provided, use it instead of API call
        if (customOnSave) {
            customOnSave(payload);
            if (!inline) {
                onClose();
            } else {
                message.success('Style Color Reference Settings updated');
            }
        } else {
            // Call API to save data
            saveStyleColorRefSettings(payload);
        }
    };

    const handleCancel = () => {
        if (!inline && onClose) {
            onClose();
        }
    };

    // Update form values when inline and values change
    const prevPayloadRef = useRef(null);
    useEffect(() => {
        if (inline && customOnSave) {
            const id = isOrderMode ? orderId : (record?.key || record?._id);
            if (id && shootTypes.length > 0) {
                const shootTypeSettings = shootTypes.map(shootType => ({
                    shootType: shootType,
                    styleCheck: checkboxStates[shootType]?.styleCheck || false,
                    colorCheck: checkboxStates[shootType]?.colorCheck || false,
                }));

                const payload = isOrderMode
                    ? {
                          orderId: orderId,
                          styleColumnName: styleColumnName,
                          colorColumnName: colorColumnName,
                          shootTypeSettings: shootTypeSettings,
                      }
                    : {
                          companyId: companyId,
                          styleColumnName: styleColumnName,
                          colorColumnName: colorColumnName,
                          shootTypeSettings: shootTypeSettings,
                      };
                
                // Only call customOnSave if payload has changed
                const payloadStr = JSON.stringify(payload);
                if (prevPayloadRef.current !== payloadStr) {
                    prevPayloadRef.current = payloadStr;
                    customOnSave(payload);
                }
            }
        }
    }, [styleColumnName, colorColumnName, checkboxStates, shootTypes, inline, customOnSave, record, orderId, isOrderMode, companyId]);

    const columns = [
        {
            title: 'Shoot Type',
            dataIndex: 'shootType',
            key: 'shootType',
            width: '40%',
        },
        {
            title: 'Style Check',
            dataIndex: 'styleCheck',
            key: 'styleCheck',
            width: '30%',
            align: 'center',
            render: (_, record) => (
                <Checkbox
                    checked={checkboxStates[record.shootType]?.styleCheck || false}
                    onChange={e => handleStyleCheckChange(record.shootType, e.target.checked)}
                />
            ),
        },
        {
            title: 'Color Check',
            dataIndex: 'colorCheck',
            key: 'colorCheck',
            width: '30%',
            align: 'center',
            render: (_, record) => (
                <Checkbox
                    checked={checkboxStates[record.shootType]?.colorCheck || false}
                    onChange={e => handleColorCheckChange(record.shootType, e.target.checked)}
                />
            ),
        },
    ];

    const tableData = shootTypes.map(shootType => ({
        key: shootType,
        shootType: shootType,
    }));

    return (
        <div
            style={{
                backgroundColor: inline ? 'transparent' : '#ffffff',
                padding: inline ? '0' : '24px',
                minHeight: inline ? 'auto' : '400px',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    marginBottom: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                    }}
                >
                    <label style={{ fontWeight: 500 }}>Style Column Name</label>
                    <Select
                        value={styleColumnName}
                        onChange={setStyleColumnName}
                        style={{ width: '100%' }}
                        options={columnOptions}
                        loading={isConfigLoading && !!companyId}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                    }}
                >
                    <label style={{ fontWeight: 500 }}>Color Column Name</label>
                    <Select
                        value={colorColumnName}
                        onChange={setColorColumnName}
                        style={{ width: '100%' }}
                        options={columnOptions}
                        loading={isConfigLoading && !!companyId}
                    />
                </div>
            </div>

            <div
                style={{
                    borderTop: '1px dashed #d9d9d9',
                    paddingTop: '16px',
                    marginBottom: '24px',
                }}
            />

            <div style={{ flex: 1, marginBottom: '24px' }}>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        bordered
                        size="middle"
                    />
                )}
            </div>

            {!inline && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        borderTop: '1px solid #f0f0f0',
                        paddingTop: '16px',
                    }}
                >
                    <Button onClick={handleCancel} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={handleSave} loading={isSaving}>
                        Save
                    </Button>
                </div>
            )}
        </div>
    );
};

export default StyleColorLibraryModal;

